import { Typography, useTheme } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { Fragment } from "react";
import ReactApexChart from "react-apexcharts";

import { DailyChartPropInterface } from "./DailyChart";

import { getTotalHours, getChartData } from "@/api";
import { convertMillisecondsToHHMMSS } from "@/utils/helpers.utils";

export const DailyChart: React.FC<DailyChartPropInterface> = ({
  offsetDate,
  limitDate,
}) => {
  const theme = useTheme();
  const { isLoading: isLoadingChartData, data: chartData } = useQuery({
    queryKey: ["chart-data", offsetDate, limitDate],
    queryFn: async () =>
      await getChartData({
        offsetDate,
        limitDate,
      }),
  });

  const { isLoading: isLoadingDailyTotalHours, data: billableTime } =
    useQuery({
      queryKey: ["total-hours", offsetDate, limitDate],
      queryFn: async () =>
        await getTotalHours({
          offsetDate,
          limitDate,
        }),
    });

  const { hours, minutes, seconds } = billableTime?.data[0] || {};

  const formattedBillableTime = [
    hours?.toString().padStart(2, "0") || "00",
    minutes?.toString().padStart(2, "0") || "00",
    seconds?.toString().padStart(2, "0") || "00",
  ].join(":");

  const apexChartOptions = {
    colors: [theme.palette.primary.main],
    toolbar: {
      export: {
        csv: {
          headerCategory: 'Date;Time',
          columnDelimiter: ';',
          formatter: convertMillisecondsToHHMMSS,
        }
      },
    },
    chart: {
      type: "bar",
      height: 200,
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: "55%",
        endingShape: "rounded",
      },
    },
    dataLabels: {
      enabled: true,
      formatter: convertMillisecondsToHHMMSS,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: (chartData?.data || [])
        .map(({ date }) => dayjs(date).format("MMM DD, YYYY")),
    },
    yaxis: {
      labels: {
        show: false,
      },
      title: {
        text: "Billable Time",
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      y: { formatter: convertMillisecondsToHHMMSS },
    },
  };

  return (
    <Fragment>
      <Typography
        variant="h6"
        align="center"
      >
        {`Total Billable Time [${dayjs(offsetDate).format('MMM DD, YYYY')} - ${dayjs(limitDate).format('MMM DD, YYYY')}] - ${formattedBillableTime}`}
      </Typography>
      {isLoadingChartData || isLoadingDailyTotalHours ? (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={apexChartOptions.chart.height}
        />
      ) : (
        <ReactApexChart
          options={apexChartOptions}
          series={[
            {
              name: "Billable Time",
              data: (chartData?.data || []).map(({ totalMilliseconds }) =>
                Number(totalMilliseconds),
              ),
            },
          ]}
          type="bar"
          height={apexChartOptions.chart.height}
        />
      )}
    </Fragment>
  );
};
